'use client';

import { PlusIcon } from '@heroicons/react/20/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { addMinutes } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { v4 } from 'uuid';
import * as z from 'zod/mini';

import { track } from '@/lib/analytics';
import { devDefaults } from '@/lib/devDefaults';
import {
  useCreateMeeting,
  useGetOccupiedSlotsFromToday,
  useUpdateMeeting,
} from '@/lib/react-query/MeetingQueries';
import { cn } from '@/lib/utils';

import type { Content } from '../../types/content.types';
import type { Meeting, Service } from '../../types/meetings.type';
import type { Product, ProductWithCategory } from '../../types/product.types';
import { formatDateInParis } from '../../utils/dateUtils';
import { doesOverlap } from '../../utils/meetingUtils';
import Alert from '../Alert';
import Button from '../Button';
import Input from '../Form/Input';
import Loading from '../Loading/Loading';
import MeetingSelection from '../MeetingSelection/MeetingSelection';
import ProductSelection from '../ProductSelection';

const ReservationSchema = z.object({
  first_name: z.string().check(z.minLength(1, { message: 'Le prénom est requis' })),
  last_name: z.string().check(z.minLength(1, { message: 'Le nom est requis' })),
  email: z
    .string()
    .check(
      z.minLength(1, { message: "L'e-mail est requis" }),
      z.email({ message: "L'e-mail n'est pas valide" }),
    ),
  phone: z.string().check(z.minLength(1, { message: 'Le téléphone est requis' })),
});

type ReservationValues = z.infer<typeof ReservationSchema>;

type Props = {
  className?: string;
  selectedProduct?: Product['slug'];
  content: Content;
  products: Array<ProductWithCategory>;
  services: Array<Service>;
  meeting?: Meeting | null;
  token?: string | null;
  initialStep?: 'date' | 'prestation';
};

const FormReservation = ({
  className = '',
  selectedProduct,
  content,
  products,
  services,
  meeting = null,
  token,
  initialStep,
}: Props) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReservationValues>({
    resolver: zodResolver(ReservationSchema),
    defaultValues: {
      first_name: meeting?.client?.first_name ?? devDefaults.first_name,
      last_name: meeting?.client?.last_name ?? devDefaults.last_name,
      email: meeting?.client?.email ?? devDefaults.email,
      phone: meeting?.client?.phone ?? devDefaults.phone,
    },
  });

  const [showPrestations, setShowPrestations] = useState<boolean>(false);
  const [showHorraires, setShowHoraires] = useState<boolean>(false);
  const [error, setError] = useState<ReactNode | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);

  const isEditing = Boolean(meeting);

  const [selectedPrestations, setSelectedPrestations] = useState<
    Array<{
      service: Service;
      product?: Product;
      id: string;
    }>
  >(() =>
    meeting?.prestations
      ? meeting.prestations.map((prestation) => ({
          service: prestation.service,
          product: prestation.product ?? undefined,
          id: prestation.id,
        }))
      : [],
  );

  const [selectedHoraire, setSelectedHoraire] = useState<Date | null>(() =>
    meeting?.start_time ? new Date(meeting.start_time) : null,
  );
  const [existingSelectedHoraire, setExistingSelectedHoraire] = useState<{
    start_time: Date;
    end_time: Date;
  } | null>(() =>
    meeting
      ? {
          start_time: new Date(meeting.start_time),
          end_time: new Date(meeting.end_time),
        }
      : null,
  );

  // Sync state when meeting data arrives after initial render (client-side fetch)
  useEffect(() => {
    if (meeting && !selectedPrestations.length) {
      setSelectedPrestations(
        (meeting.prestations ?? []).map((prestation) => ({
          service: prestation.service,
          product: prestation.product ?? undefined,
          id: prestation.id,
        })),
      );
      setSelectedHoraire(new Date(meeting.start_time));
      setExistingSelectedHoraire({
        start_time: new Date(meeting.start_time),
        end_time: new Date(meeting.end_time),
      });
    }
  }, [meeting, selectedPrestations.length]);

  // Deep-link a specific edit step (from the "Avant d'annuler" interception modal)
  useEffect(() => {
    if (initialStep === 'prestation') {
      setShowPrestations(true);
    } else if (initialStep === 'date') {
      setShowHoraires(true);
    }
  }, [initialStep]);

  useEffect(() => {
    const meetingButtons = document.querySelectorAll('.meeting-button');

    meetingButtons.forEach((btn) => {
      (btn as HTMLElement).style.display = 'none';
    });

    return () => {
      meetingButtons.forEach((btn) => {
        (btn as HTMLElement).style.display = '';
      });
    };
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      const product = products.find((p) => p.slug === selectedProduct);
      if (product) {
        setSelectedPrestations([
          {
            service: services.find((service) => service.has_products)!,
            product: product,
            id: v4(),
          },
        ]);
      }
    }
  }, [services, selectedProduct, products]);

  const hasEditChanges = useMemo(() => {
    if (!meeting || !selectedHoraire || !selectedPrestations.length) {
      return false;
    }

    const hasSameHoraire =
      existingSelectedHoraire?.start_time.getTime() === selectedHoraire.getTime();

    const hasExactPrestations =
      selectedPrestations.length === (meeting.prestations?.length ?? 0) &&
      selectedPrestations.every((prestation) =>
        meeting.prestations?.some((p) => p.id === prestation.id),
      );

    return !hasSameHoraire || !hasExactPrestations;
  }, [meeting, selectedHoraire, selectedPrestations, existingSelectedHoraire?.start_time]);

  const instagram = useMemo(
    () => content.instagram?.filter((insta) => insta.type === 'piercing')[0],
    [content.instagram],
  );

  const handleSelectPrestation = useCallback(
    (service: Service, product?: Product) => {
      setSelectedPrestations([
        ...selectedPrestations,
        {
          service: service,
          product: product,
          id: v4(),
        },
      ]);

      setShowPrestations(false);
      window.scrollTo({ top: 100, behavior: 'instant' });

      track('RendezVousPrestationSelected', {
        service: service.name,
        product: product?.name ?? '',
      });
    },
    [selectedPrestations],
  );

  const handleRemovePrestation = useCallback(
    (id: string) => {
      setSelectedPrestations(selectedPrestations.filter((prestation) => prestation.id !== id));
    },
    [selectedPrestations],
  );

  const handleSelectHoraire = useCallback((horaire: Date | null) => {
    setSelectedHoraire(horaire);
  }, []);

  const cancelNewHorraires = useCallback(() => {
    setShowHoraires(false);
    setSelectedHoraire(existingSelectedHoraire?.start_time ?? null);
  }, [existingSelectedHoraire?.start_time]);

  const handleRemoveHoraire = useCallback(() => {
    setShowHoraires(true);
  }, []);

  const errorMessage = useMemo(() => {
    return (
      <p>
        Une erreur est survenue lors de la creation du rendez-vous. Veuillez me contacter via{' '}
        <a href={content.contact?.email ?? ''} className="font-semibold underline">
          E-mail
        </a>{' '}
        ou{' '}
        <a href={instagram?.url ?? ''} className="font-semibold underline">
          Instagram
        </a>
      </p>
    );
  }, [content.contact?.email, instagram?.url]);

  const errorDuplicatePhone = useMemo(() => {
    return (
      <>
        <p>Ce numéro de téléphone est déjà associé à un autre compte avec un e-mail différent.</p>
        <p className="mt-2">
          Veuillez utiliser l'e-mail correspondant ou me contacter via{' '}
          <a href={content.contact?.email ?? ''} className="font-semibold underline">
            E-mail
          </a>{' '}
          ou{' '}
          <a href={instagram?.url ?? ''} className="font-semibold underline">
            Instagram
          </a>
          .
        </p>
      </>
    );
  }, [content.contact?.email, instagram?.url]);

  const createMutation = useCreateMeeting();
  const updateMutation = useUpdateMeeting();

  const onSubmit = handleSubmit(async (client) => {
    if (!selectedHoraire) {
      toast.error('Veuillez sélectionner un horaire');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const prestations = selectedPrestations.map((prestation) => ({
        service_id: prestation.service.id,
        product_id: prestation.product?.id,
      }));

      if (isEditing && meeting && token) {
        // Update existing meeting
        const result = await updateMutation.mutateAsync({
          id: meeting.id,
          token,
          prestations,
          start_time: selectedHoraire,
          send_notification: true,
        });

        // Navigate to confirmation on success
        if (result.success && result.data) {
          track('RendezVousFormSubmit', { prestationCount: String(selectedPrestations.length) });
          navigate({
            to: '/rendez-vous/confirmation',
            search: { id: result.data.id, token },
          });
        }
      } else {
        // Create new meeting
        const result = await createMutation.mutateAsync({
          prestations,
          start_time: selectedHoraire,
          send_notification: true,
          client: {
            first_name: client.first_name,
            last_name: client.last_name,
            email: client.email,
            phone: client.phone,
          },
        });

        // Check for phone duplicate error
        if (result.error?.message.includes('phone_1 dup key')) {
          setError(errorDuplicatePhone);
        } else if (result.success && result.data) {
          track('RendezVousFormSubmit', { prestationCount: String(selectedPrestations.length) });
          // Navigate to confirmation on success
          navigate({
            to: '/rendez-vous/confirmation',
            search: {
              id: result.data.id,
              token: result.data.access_token || undefined,
            },
          });
        } else {
          console.warn('⚠️ Navigation not triggered because:', {
            success: result.success,
            hasData: !!result.data,
          });
        }
      }
    } catch (err: unknown) {
      const errorObj = err as { message?: string };

      if (errorObj.message?.includes('phone_1 dup key')) {
        setError(errorDuplicatePhone);
      } else if (errorObj.message?.toLowerCase().includes('overlap')) {
        toast.error(
          'Ce créneau chevauche un rendez-vous existant. Veuillez choisir un autre horaire.',
        );
        setShowHoraires(true);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSending(false);
    }
  });

  const totalDuration = useMemo(() => {
    return selectedPrestations.reduce(
      (total, prestation) => total + prestation.service.duration,
      0,
    );
  }, [selectedPrestations]);

  const originalDuration = useMemo(() => {
    if (!meeting?.prestations) {
      return 0;
    }

    return meeting.prestations.reduce((total, p) => total + p.service.duration, 0);
  }, [meeting?.prestations]);

  const durationChanged = isEditing && totalDuration > 0 && totalDuration !== originalDuration;

  const { data: occupiedSlots = [] } = useGetOccupiedSlotsFromToday();

  const selectedSlotOverlaps = useMemo(() => {
    if (!selectedHoraire || !totalDuration) return false;

    const slotEnd = addMinutes(selectedHoraire, totalDuration);
    // Exclude the current meeting's own slot from overlap check
    const otherMeetings = existingSelectedHoraire
      ? occupiedSlots.filter((slot) => {
          const slotStart = new Date(slot.start_time).getTime();
          return slotStart !== existingSelectedHoraire.start_time.getTime();
        })
      : occupiedSlots;

    return doesOverlap(
      selectedHoraire,
      slotEnd,
      otherMeetings.map((s) => ({ start_time: s.start_time, end_time: s.end_time })),
    );
  }, [selectedHoraire, totalDuration, occupiedSlots, existingSelectedHoraire]);

  const [durationConfirmed, setDurationConfirmed] = useState(false);

  // Reset confirmation when duration changes again
  useEffect(() => {
    if (durationChanged) {
      setDurationConfirmed(false);
    }
  }, [totalDuration]);

  // When duration changes in edit mode, force re-opening time picker (only if not yet confirmed)
  useEffect(() => {
    if (durationChanged && !showHorraires && !durationConfirmed) {
      setShowHoraires(true);
    }
  }, [durationChanged, showHorraires, durationConfirmed]);

  const showDuration = useCallback(() => {
    if (totalDuration < 60) {
      return `${totalDuration} minutes`;
    } else if (totalDuration === 60) {
      return '1 heure';
    }

    return `${Math.floor(totalDuration / 60)} heures et ${totalDuration % 60} minutes`;
  }, [totalDuration]);

  return (
    <div className={cn(className)}>
      {!products.length || !services.length ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-16">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                1. Sélectionner une {selectedPrestations.length && showPrestations ? 'autre ' : ''}{' '}
                prestation
              </h2>
              {selectedPrestations.length && showPrestations ? (
                <Button color="secondary" onClick={() => setShowPrestations(false)}>
                  Annuler
                </Button>
              ) : null}
            </div>
            {!selectedPrestations.length || showPrestations ? (
              <>
                <ProductSelection
                  products={products}
                  services={services}
                  onSelect={handleSelectPrestation}
                />
                {selectedPrestations.length && showPrestations ? (
                  <Button
                    color="secondary"
                    onClick={() => setShowPrestations(false)}
                    className="mt-4"
                  >
                    Annuler
                  </Button>
                ) : null}
              </>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  {selectedPrestations.map((prestation) => {
                    const price = prestation.product
                      ? prestation.product.price
                      : prestation.service.price;
                    const name = prestation.product
                      ? `${prestation.service.name} - ${prestation.product.name}`
                      : prestation.service.name;

                    return (
                      <div
                        key={prestation.id}
                        className="flex items-center justify-between rounded-lg border border-amber-200 bg-white/50 p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">{name}</p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {prestation.service.duration} min
                            {price ? ` · ${price} €` : ' · Gratuit'}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="ml-3 cursor-pointer text-xs font-medium text-red-600 hover:text-red-800"
                          onClick={() => handleRemovePrestation(prestation.id)}
                        >
                          Supprimer
                        </button>
                      </div>
                    );
                  })}
                </div>

                <Button onClick={() => setShowPrestations(true)} className="md:w-fit md:self-end">
                  <span className="flex items-center">
                    <PlusIcon className="mr-1.5 h-5 w-5" />
                    <span>Ajouter une autre prestation</span>
                  </span>
                </Button>

                <div className="flex items-center justify-end gap-1 text-sm">
                  <span className="text-gray-600">Total:</span>
                  <span className="text-base font-semibold text-gray-900">
                    {selectedPrestations.reduce((total, prestation) => {
                      if (prestation.product) {
                        return total + prestation.product.price;
                      }
                      return total + prestation.service.price;
                    }, 0)}{' '}
                    €
                  </span>
                </div>
              </div>
            )}
          </div>

          {selectedPrestations.length &&
          ((isEditing && showHorraires) || (!isEditing && !selectedHoraire) || showHorraires) ? (
            <div>
              <h2 className="mb-2 text-xl font-semibold">2. Sélectionner un horaire</h2>
              {durationChanged ? (
                <Alert variant="warning" className="mb-4">
                  La durée des prestations a changé ({showDuration()}). Veuillez confirmer ou
                  choisir un nouveau créneau horaire.
                </Alert>
              ) : (
                <p className="mb-4 text-sm text-neutral-600">Durée estimée: {showDuration()}</p>
              )}
              <MeetingSelection
                horaires={content.horaire ?? []}
                onSelect={handleSelectHoraire}
                totalDuration={totalDuration}
                defaultSelect={selectedHoraire}
                existingSelectedHoraire={existingSelectedHoraire}
              />

              {isEditing || (showHorraires && Boolean(selectedHoraire)) ? (
                <div className="mt-4 flex flex-col gap-3">
                  {selectedSlotOverlaps ? (
                    <Alert variant="danger">
                      Ce créneau chevauche un autre rendez-vous avec la nouvelle durée. Veuillez
                      choisir un autre horaire.
                    </Alert>
                  ) : null}
                  <div className="flex gap-2">
                    <Button color="secondary" onClick={cancelNewHorraires}>
                      Annuler
                    </Button>
                    <Button
                      color="primary"
                      disabled={selectedSlotOverlaps}
                      onClick={() => {
                        setShowHoraires(false);
                        setDurationConfirmed(true);
                        track('RendezVousHoraireSelected');
                      }}
                    >
                      Confirmer
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {!showHorraires && selectedHoraire ? (
            <div>
              <h2 className="mb-2 text-xl font-semibold">2. Sélectionner un horaire</h2>
              <h3 className="text-lg font-semibold">Date sélectionnée:</h3>
              <div className="flex justify-between gap-x-6 py-5">
                <span className="capitalize">
                  {formatDateInParis(selectedHoraire, 'EEEE dd MMMM yyyy HH:mm')}
                  <span className="pl-2 text-sm text-gray-500 normal-case">(heure de Paris)</span>
                </span>
                <Button color="neutral" onClick={handleRemoveHoraire}>
                  Modifier
                </Button>
              </div>
            </div>
          ) : null}

          {selectedPrestations.length && !showHorraires && !!selectedHoraire ? (
            <div>
              <h2 className="mb-2 text-xl font-semibold">3. Coordonnées</h2>
              <form className="flex flex-col gap-4" onSubmit={onSubmit}>
                {error ? <Alert variant="danger">{error}</Alert> : null}
                <div className="max-w-xl lg:max-w-lg">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="first-name"
                        className="block text-sm leading-6 font-semibold text-gray-900"
                      >
                        Prénom
                      </label>
                      <div className="mt-2.5">
                        <Input
                          type="text"
                          id="first-name"
                          autoComplete="given-name"
                          {...register('first_name')}
                        />
                        {errors.first_name && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.first_name.message as string}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="last-name"
                        className="block text-sm leading-6 font-semibold text-gray-900"
                      >
                        Nom
                      </label>
                      <div className="mt-2.5">
                        <Input
                          type="text"
                          id="last-name"
                          autoComplete="family-name"
                          {...register('last_name')}
                        />

                        {errors.last_name && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.last_name.message as string}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="email"
                        className="block text-sm leading-6 font-semibold text-gray-900"
                      >
                        E-mail
                      </label>
                      <div className="mt-2.5">
                        <Input
                          type="email"
                          id="email"
                          autoComplete="email"
                          {...register('email')}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.email.message as string}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="phone"
                        className="block text-sm leading-6 font-semibold text-gray-900"
                      >
                        Téléphone
                      </label>
                      <div className="mt-2.5">
                        <Input type="tel" id="phone" autoComplete="phone" {...register('phone')} />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.phone.message as string}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 flex justify-end">
                    {isEditing && meeting ? (
                      <Link
                        to="/rendez-vous/confirmation"
                        search={{ id: meeting.id, token: token || undefined }}
                        className="mr-2"
                      >
                        <Button color="secondary">Annuler la modification</Button>
                      </Link>
                    ) : null}
                    <Button
                      type="submit"
                      isLoading={isSending}
                      disabled={isSending || (isEditing && !hasEditChanges)}
                    >
                      {isEditing ? 'Modifier' : 'Réserver'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default FormReservation;
